import { create } from 'zustand';

// Role types
export type PhysicianRole = 'primary-care-md' | 'psychiatry-md';
export type APCRole = 'primary-care-apc' | 'psychiatry-apc' | 'palliative-care-apc' | 'teletriage';
export type RoleType = PhysicianRole | APCRole;

export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
export type EmploymentStatus = 'full-time' | 'part-time';
export type Track = 'physician' | 'non-physician';

// Measure met/unmet state
export interface MeasureState {
  id: string;
  passed: boolean;
}

// Calculator state
interface CalculatorState {
  role: RoleType;
  quarter: Quarter;
  employmentStatus: EmploymentStatus;
  daysPerWeek: number;
  outcomeMeasures: MeasureState[];
  consistencyMeasures: MeasureState[];
  gatePassed: boolean; // ACO Visits gate status for physicians
}

// Main store state
interface EquipStore {
  // Track and role selection (session persistent)
  currentTrack: Track;
  selectedRole: RoleType;
  selectedQuarter: Quarter;

  // Calculator state (session persistent)
  calculator: CalculatorState;

  // Actions
  setTrack: (track: Track) => void;
  setRole: (role: RoleType) => void;
  setQuarter: (quarter: Quarter) => void;
  setEmploymentStatus: (status: EmploymentStatus) => void;
  setDaysPerWeek: (days: number) => void;
  toggleMeasure: (measureId: string, type: 'outcome' | 'consistency') => void;
  toggleGate: () => void;
  setAllMeasuresPassing: () => void;
  resetMeasures: () => void;
  loadPreset: (preset: 'all-pass' | 'one-outcome-miss' | 'q1-ramp-up' | 'consistency-floor' | 'single-measure-risk') => void;
  initializeMeasuresForRole: (role: RoleType, quarter: Quarter) => void;
  setOutcomeMeasures: (measures: MeasureState[]) => void;
}

// Measure configurations by role and quarter
// For physicians: ACO Visits is now a gate measure (not weighted outcome)
// Q1 physicians have no outcome measures (gate + consistency only)
// Q2+ physicians have outcome measures without ACO Visits
export const getOutcomeMeasures = (role: RoleType, quarter: Quarter): MeasureState[] => {
  switch (role) {
    case 'primary-care-md':
      // Q1: No outcome measures - gate only, consistency at 100%
      if (quarter === 'Q1') {
        return [];
      }
      if (quarter === 'Q2') {
        return [
          { id: 'a1c', passed: true },
          { id: 'colorectal', passed: true },
          { id: 'phq9', passed: true },
        ];
      }
      if (quarter === 'Q3') {
        return [
          { id: 'a1c', passed: true },
          { id: 'bcs', passed: true },
          { id: 'colorectal', passed: true },
          { id: 'phq9', passed: true },
        ];
      }
      // Q4
      return [
        { id: 'a1c', passed: true },
        { id: 'bcs', passed: true },
        { id: 'colorectal', passed: true },
        { id: 'phq9', passed: true },
        { id: 'htn', passed: true },
      ];
    case 'psychiatry-md':
      // Psychiatrist: PHQ9 is the only outcome measure, all quarters (same as Psych Med APC)
      return [{ id: 'phq9', passed: true }];
    case 'primary-care-apc':
      if (quarter === 'Q1') {
        return [{ id: 'awv', passed: true }];
      } else if (quarter === 'Q2') {
        return [
          { id: 'awv', passed: true },
          { id: 'a1c', passed: true },
          { id: 'colorectal', passed: true },
          { id: 'phq9', passed: true },
        ];
      } else if (quarter === 'Q3') {
        return [
          { id: 'a1c', passed: true },
          { id: 'bcs', passed: true },
          { id: 'colorectal', passed: true },
          { id: 'phq9', passed: true },
        ];
      } else {
        return [
          { id: 'a1c', passed: true },
          { id: 'bcs', passed: true },
          { id: 'colorectal', passed: true },
          { id: 'phq9', passed: true },
          { id: 'htn', passed: true },
        ];
      }
    case 'psychiatry-apc':
      return [{ id: 'phq9', passed: true }];
    case 'palliative-care-apc':
      return [{ id: 'acp', passed: true }];
    case 'teletriage':
      return [{ id: 'ed-sendout', passed: true }];
    default:
      return [];
  }
};

export const getConsistencyMeasures = (role: RoleType): MeasureState[] => {
  if (role === 'teletriage') {
    return [
      { id: 'coding', passed: true },
      { id: 'documentation', passed: true },
    ];
  }
  return [
    { id: 'pims', passed: true },
    { id: 'coding', passed: true },
    { id: 'documentation', passed: true },
  ];
};

// Maximum award configurations
export const getMaxAward = (role: RoleType, employmentStatus: EmploymentStatus, daysOrHoursPerWeek: number): number => {
  const fullTimeMaximum: Record<RoleType, number> = {
    'primary-care-md': 4000,
    'psychiatry-md': 4000,
    'primary-care-apc': 3000,
    'psychiatry-apc': 3000,
    'palliative-care-apc': 3000,
    'teletriage': 1500,
  };

  if (employmentStatus === 'full-time') {
    return fullTimeMaximum[role];
  }

  // Teletriage uses hourly rate ($37.50/hour, max 40 hours)
  if (role === 'teletriage') {
    return Math.round(37.5 * Math.min(daysOrHoursPerWeek, 40));
  }

  // All other roles use per-day rate
  const perDayRate: Record<RoleType, number> = {
    'primary-care-md': 800,
    'psychiatry-md': 800,
    'primary-care-apc': 600,
    'psychiatry-apc': 600,
    'palliative-care-apc': 600,
    'teletriage': 300,
  };
  return perDayRate[role] * daysOrHoursPerWeek;
};

// Measure display names
export const measureNames: Record<string, string> = {
  'aco-visits': 'ACO Visits',
  'aco': 'ACO Visits',
  'awv': 'AWV',
  'a1c': 'A1c',
  'bcs': 'BCS',
  'colorectal': 'CRCS',
  'phq9': 'PHQ-9',
  'htn': 'HTN',
  'acp': 'ACP',
  'ed-sendout': 'ED Send-Out',
  'pims': 'PIMs',
  'coding': 'Coding',
  'documentation': 'Sig Stnds',
};

// Role display names
export const roleNames: Record<RoleType, string> = {
  'primary-care-md': 'Primary Care Physician',
  'psychiatry-md': 'Psychiatrist',
  'primary-care-apc': 'Primary Care APC',
  'psychiatry-apc': 'Psychiatry APC',
  'palliative-care-apc': 'Palliative Care APC',
  'teletriage': 'Teletriage',
};

// Create the store
export const useEquipStore = create<EquipStore>((set, get) => ({
  // Initial state
  currentTrack: 'physician',
  selectedRole: 'primary-care-md',
  selectedQuarter: 'Q1',

  calculator: {
    role: 'primary-care-md',
    quarter: 'Q1',
    employmentStatus: 'full-time',
    daysPerWeek: 5,
    outcomeMeasures: getOutcomeMeasures('primary-care-md', 'Q1'),
    consistencyMeasures: getConsistencyMeasures('primary-care-md'),
    gatePassed: true,
  },

  // Actions
  setTrack: (track) => set({ currentTrack: track }),

  setRole: (role) => {
    const { selectedQuarter, calculator } = get();
    const prevRole = calculator.role;
    const isPartTime = calculator.employmentStatus === 'part-time';

    // When switching between teletriage (hours) and other roles (days) while part-time,
    // reset to a sensible default so the slider value makes sense for the new unit
    let daysPerWeek = calculator.daysPerWeek;
    if (isPartTime) {
      const switchingToTeletriage = role === 'teletriage' && prevRole !== 'teletriage';
      const switchingFromTeletriage = role !== 'teletriage' && prevRole === 'teletriage';
      if (switchingToTeletriage) {
        daysPerWeek = 30; // 30 hours/week default
      } else if (switchingFromTeletriage) {
        daysPerWeek = 3; // 3 days/week default
      }
    }

    set({
      selectedRole: role,
      calculator: {
        ...calculator,
        role,
        daysPerWeek,
        outcomeMeasures: getOutcomeMeasures(role, selectedQuarter),
        consistencyMeasures: getConsistencyMeasures(role),
      },
    });
  },

  setQuarter: (quarter) => {
    const { selectedRole } = get();
    set({
      selectedQuarter: quarter,
      calculator: {
        ...get().calculator,
        quarter,
        outcomeMeasures: getOutcomeMeasures(selectedRole, quarter),
      },
    });
  },

  setEmploymentStatus: (status) => {
    const { calculator } = get();
    let daysPerWeek = calculator.daysPerWeek;
    if (status === 'full-time') {
      daysPerWeek = calculator.role === 'teletriage' ? 40 : 5;
    } else if (status === 'part-time') {
      // Set sensible part-time defaults based on role
      daysPerWeek = calculator.role === 'teletriage' ? 30 : 3;
    }
    set({
      calculator: {
        ...calculator,
        employmentStatus: status,
        daysPerWeek,
      },
    });
  },

  setDaysPerWeek: (days) => set({
    calculator: {
      ...get().calculator,
      daysPerWeek: days,
    },
  }),

  toggleMeasure: (measureId, type) => {
    const { calculator } = get();
    const measures = type === 'outcome' ? calculator.outcomeMeasures : calculator.consistencyMeasures;
    const updatedMeasures = measures.map((m) =>
      m.id === measureId ? { ...m, passed: !m.passed } : m
    );

    set({
      calculator: {
        ...calculator,
        [type === 'outcome' ? 'outcomeMeasures' : 'consistencyMeasures']: updatedMeasures,
      },
    });
  },

  toggleGate: () => {
    const { calculator } = get();
    set({
      calculator: {
        ...calculator,
        gatePassed: !calculator.gatePassed,
      },
    });
  },

  setAllMeasuresPassing: () => {
    const { calculator } = get();
    set({
      calculator: {
        ...calculator,
        outcomeMeasures: calculator.outcomeMeasures.map((m) => ({ ...m, passed: true })),
        consistencyMeasures: calculator.consistencyMeasures.map((m) => ({ ...m, passed: true })),
        gatePassed: true,
      },
    });
  },

  resetMeasures: () => {
    const { selectedRole } = get();
    set({
      selectedQuarter: 'Q1',
      calculator: {
        ...get().calculator,
        quarter: 'Q1',
        employmentStatus: 'full-time',
        daysPerWeek: 5,
        outcomeMeasures: getOutcomeMeasures(selectedRole, 'Q1'),
        consistencyMeasures: getConsistencyMeasures(selectedRole),
        gatePassed: true,
      },
    });
  },

  loadPreset: (preset) => {
    const { calculator, selectedRole, selectedQuarter: _selectedQuarter } = get();
    void _selectedQuarter; // May be used for quarter-specific presets

    switch (preset) {
      case 'all-pass':
        get().setAllMeasuresPassing();
        break;
      case 'one-outcome-miss':
        set({
          calculator: {
            ...calculator,
            outcomeMeasures: calculator.outcomeMeasures.map((m, i) =>
              i === 0 ? { ...m, passed: false } : { ...m, passed: true }
            ),
            consistencyMeasures: calculator.consistencyMeasures.map((m) => ({ ...m, passed: true })),
          },
        });
        break;
      case 'q1-ramp-up':
        get().setQuarter('Q1');
        get().setAllMeasuresPassing();
        break;
      case 'consistency-floor':
        set({
          calculator: {
            ...calculator,
            outcomeMeasures: calculator.outcomeMeasures.map((m) => ({ ...m, passed: false })),
            consistencyMeasures: calculator.consistencyMeasures.map((m) => ({ ...m, passed: true })),
          },
        });
        break;
      case 'single-measure-risk':
        // Switch to a single-measure role in Q1
        if (selectedRole === 'primary-care-md' || selectedRole === 'primary-care-apc') {
          get().setQuarter('Q1');
        }
        const newOutcomes = getOutcomeMeasures(selectedRole, 'Q1');
        set({
          calculator: {
            ...calculator,
            quarter: 'Q1',
            outcomeMeasures: newOutcomes.map((m) => ({ ...m, passed: false })),
            consistencyMeasures: calculator.consistencyMeasures.map((m) => ({ ...m, passed: true })),
          },
        });
        break;
    }
  },

  initializeMeasuresForRole: (role, quarter) => {
    set({
      selectedRole: role,
      selectedQuarter: quarter,
      calculator: {
        ...get().calculator,
        role,
        quarter,
        outcomeMeasures: getOutcomeMeasures(role, quarter),
        consistencyMeasures: getConsistencyMeasures(role),
        gatePassed: true,
      },
    });
  },

  setOutcomeMeasures: (measures) => {
    set({ calculator: { ...get().calculator, outcomeMeasures: measures } });
  },
}));

// Helper to check if a role has the ACO Visits gate requirement.
// Only PC Physicians have the gate; Psychiatrists do not.
export function isPhysicianRole(role: RoleType): boolean {
  return role === 'primary-care-md';
}

// Calculation helpers
export function calculateOutcomePercentage(
  measures: MeasureState[],
  outcomeWeight: number = 60
): number {
  if (measures.length === 0) return 0;
  const passingCount = measures.filter((m) => m.passed).length;
  const percentPerMeasure = outcomeWeight / measures.length;
  return passingCount * percentPerMeasure;
}

export function calculateConsistencyPercentage(
  measures: MeasureState[],
  consistencyWeight: number = 40
): number {
  if (measures.length === 0) return 0;
  const passingCount = measures.filter((m) => m.passed).length;
  const percentPerMeasure = consistencyWeight / measures.length;
  return passingCount * percentPerMeasure;
}

export interface AwardResult {
  outcomePercentage: number;
  consistencyPercentage: number;
  totalPercentage: number;
  outcomeAward: number;
  consistencyAward: number;
  totalAward: number;
  gatePassed: boolean;
  outcomeWeight: number;
  consistencyWeight: number;
}

export function calculateAward(
  outcomeMeasures: MeasureState[],
  consistencyMeasures: MeasureState[],
  maxAward: number,
  role?: RoleType,
  quarter?: Quarter,
  gatePassed?: boolean
): AwardResult {
  const isPhysician = role ? isPhysicianRole(role) : false;
  const isQ1 = quarter === 'Q1';
  const gateStatus = gatePassed ?? true;

  // Determine weights based on role/quarter/gate
  let outcomeWeight = 60;
  let consistencyWeight = 40;

  if (isPhysician) {
    // Gate not passed: $0 total
    if (!gateStatus) {
      return {
        outcomePercentage: 0,
        consistencyPercentage: 0,
        totalPercentage: 0,
        outcomeAward: 0,
        consistencyAward: 0,
        totalAward: 0,
        gatePassed: false,
        outcomeWeight: 0,
        consistencyWeight: 0,
      };
    }

    // Q1 physician with gate passed: consistency only at 100%
    if (isQ1) {
      outcomeWeight = 0;
      consistencyWeight = 100;
    }
    // Q2+ physician with gate passed: standard 60/40 (already set)
  }

  const outcomePercentage = calculateOutcomePercentage(outcomeMeasures, outcomeWeight);
  const consistencyPercentage = calculateConsistencyPercentage(consistencyMeasures, consistencyWeight);
  const totalPercentage = outcomePercentage + consistencyPercentage;

  const outcomeAward = Math.round((outcomePercentage / 100) * maxAward);
  const consistencyAward = Math.round((consistencyPercentage / 100) * maxAward);
  const totalAward = outcomeAward + consistencyAward;

  return {
    outcomePercentage,
    consistencyPercentage,
    totalPercentage,
    outcomeAward,
    consistencyAward,
    totalAward,
    gatePassed: gateStatus,
    outcomeWeight,
    consistencyWeight,
  };
}
